import { type GtmSupport, useGtm } from '@gtm-support/vue-gtm';
import { setTag } from '@sentry/vue';
import { err, ok, Result } from 'neverthrow';
import { consola } from './consola';
import { trackSentryError } from './SentryHelper';
import type SubscriptionOrder from '@/utilities/SubscriptionOrder';
import type { BillingOccurence } from '@/models/BillingOccurence';
import type { PartialRecord } from '@/models/PartialRecord';
import type { PlanCategory } from '@/models/PlanCategory';
import type { ExperienceLevel } from '@/models/ExperienceLevel';
import { getBillingOccurence, getISOCurrency } from '@/models/PlanVariant';
import { getDiscountedPriceInCents, type Product } from '@/models/Product';
import type { SignupType } from '@/models/SignupType';
import type { Cart } from '@/models/Cart';
import { getFirstChargePriceInCents } from '@/models/PaymentPeriod';
import type { PaymentMethod } from '@/utilities/PaymentMethods';
import {
	type LegacyTrackingEventMap,
	type LegacyTrackingEventName,
	paymentTypes,
	type PurchaseCheckout,
	purchaseTypes,
	type TrackingContext,
	type TrackingEventMap,
	type TrackingEventName,
} from '@/models/TrackingEvent';
import { plausible } from '@/plugins/plausible/setup';
import i18n from '@/plugins/i18n';
import { TrackingErrorCodes } from '@/enums/ErrorCodes';
import { useAuthStore } from '@/store/auth';
import { useMainStore } from '@/store/main';
import { useProductsStore } from '@/store/products';

type TrackingProvider = 'plausible';

type PlausibleProps = Record<string, string | number | boolean>;

const billingOccurenceGoals: Record<string, PartialRecord<BillingOccurence, Record<TrackingProvider, string>>> = {
	piano: {
		Monthly: {
			plausible: 'Piano-1M',
		},
		Biannually: {
			plausible: 'Piano-6M',
		},
		Yearly: {
			plausible: 'Piano-12M',
		},
	},
	guitar: {
		Monthly: {
			plausible: 'Guitar-1M',
		},
		Biannually: {
			plausible: 'Guitar-6M',
		},
		Yearly: {
			plausible: 'Guitar-12M',
		},
	},
	drums: {
		Monthly: {
			plausible: 'Drums-1M',
		},
		Biannually: {
			plausible: 'Drums-6M',
		},
		Yearly: {
			plausible: 'Drums-12M',
		},
	},
};

export const getPlausibleGoal = (order: SubscriptionOrder): string => {
	const plan = order.getPlan();

	if (!plan.CustomFields) {
		return '';
	}

	const categoryIdMap: Record<PlanCategory, string> = {
		piano: billingOccurenceGoals.piano[order.billingOccurence.id]?.plausible || 'Subscription Piano',
		guitar: billingOccurenceGoals.guitar[order.billingOccurence.id]?.plausible || 'Subscription Guitar',
		drums: billingOccurenceGoals.drums[order.billingOccurence.id]?.plausible || 'Subscription Drums',
		// TODO: Check if we want to use different goal for plus
		plus: 'Subscriptions Plus',
		combo: 'Subscription Combo',
	};

	const planCategory: PlanCategory = plan.CustomFields.category;

	return categoryIdMap[planCategory];
};

const reportTrackingError = (event: string, cause: unknown) => {
	trackSentryError({
		errorCode: TrackingErrorCodes.TRACKING_FAILED,
		cause,
	});
	consola.info(`Failed to track ${event}`, cause);
};

const getGtm = (): Result<GtmSupport, Error> => {
	const gtm = useGtm();

	return gtm ? ok(gtm) : err(new Error('GTM plugin is not installed'));
};

const getTrackingContext = (): TrackingContext => ({
	user_id: useAuthStore().user?.userId ?? null,
	portal: useProductsStore().portalId,
	signup_type: useMainStore().signupType,
	locale: i18n.global.locale,
});

const isPlausiblePropValue = (entry: [string, unknown]): entry is [string, PlausibleProps[string]] => ['string', 'number', 'boolean'].includes(typeof entry[1]);

const toPlausibleProps = (payload: Record<string, unknown>): PlausibleProps => Object.fromEntries(Object.entries(payload).filter(isPlausiblePropValue));

const trackPlausibleEvent = (name: string, props: PlausibleProps | null) => {
	Result.fromThrowable(() => plausible.trackEvent(name, props ? { props } : undefined))()
		.mapErr((cause) => reportTrackingError(name, cause));
};

const sendEvent = (name: string, params: Record<string, unknown>, getContext: () => TrackingContext) => {
	Result.fromThrowable(getContext)().match(
		({ user_id, ...anonymousContext }) => {
			getGtm()
				.andThen((gtm) => Result.fromThrowable(() => {
					// GTM merges ecommerce objects across pushes, so GA4 requires clearing the previous one first.
					if ('ecommerce' in params) {
						gtm.push({ ecommerce: null });
					}

					gtm.push({
						event: name, user_id, ...anonymousContext, ...params,
					});
				})())
				.mapErr((cause) => reportTrackingError(name, cause));

			// Plausible runs without consent, so it must never receive a user identifier.
			trackPlausibleEvent(name, toPlausibleProps({ ...anonymousContext, ...params }));
		},
		(cause) => reportTrackingError(name, cause),
	);
};

export const trackEvent = <Name extends TrackingEventName>(
	name: Name,
	params: TrackingEventMap[Name] & { [Key in keyof TrackingContext]?: never },
) => sendEvent(name, params, getTrackingContext);

export const trackCheckoutStarted = (signupType: SignupType) => {
	if (signupType === 'coupon') {
		return;
	}

	trackEvent('checkout_started', {});
};

export const trackCheckoutChooseCourse = (product: Product, level: ExperienceLevel) => {
	const { planVariant } = product;

	Result.fromThrowable(() => ({
		value: Math.round(getDiscountedPriceInCents(product)) / 100,
		currency: getISOCurrency(planVariant),
		level,
		...(planVariant.CustomFields.planType === 'single' ? { instrument: planVariant.CustomFields.category } : {}),
		purchase_type: purchaseTypes[getBillingOccurence(planVariant)],
	}))().match(
		(params) => trackEvent('checkout_choose_course', params),
		(cause) => reportTrackingError('checkout_choose_course', cause),
	);
};

export const trackCheckoutChoosePayment = (paymentMethod: PaymentMethod) => {
	trackEvent('checkout_choose_payment', {
		payment_type: paymentTypes[paymentMethod],
	});
};

const isCampaignCode = (code: string, campaignCode: string | undefined) => code.toLowerCase() === campaignCode?.toLowerCase();

export const trackDiscountCodeApplied = (code: string, campaignCode: string | undefined) => {
	if (isCampaignCode(code, campaignCode)) {
		return;
	}

	trackEvent('coupon_applied', {
		code_type: 'discount_code',
	});
};

const getPurchaseCheckoutKey = (orderId: string) => `tracking.purchase.${orderId}`;

export const rememberCheckoutForPurchase = (orderId: string, cart: Cart | null, paymentMethod: PaymentMethod | undefined) => {
	Result.fromThrowable(() => {
		const { signupType } = useMainStore();

		if (!cart || !paymentMethod || signupType === 'coupon') {
			return;
		}

		const checkout: PurchaseCheckout = {
			value: Math.round(getFirstChargePriceInCents({ planVariant: cart.planVariant, discount: cart.discount })) / 100,
			currency: getISOCurrency(cart.planVariant),
			coupon: cart.CouponCode && !isCampaignCode(cart.CouponCode, cart.billwerkCouponCode) ? 'discount_code' : null,
			paymentType: paymentTypes[paymentMethod],
			portal: useProductsStore().portalId,
			signupType,
		};

		sessionStorage.setItem(getPurchaseCheckoutKey(orderId), JSON.stringify(checkout));
	})().mapErr((cause) => reportTrackingError('purchase', cause));
};

export const trackPurchase = (order: SubscriptionOrder) => {
	const checkoutKey = getPurchaseCheckoutKey(order.getID());

	Result.fromThrowable(() => {
		const storedCheckout = sessionStorage.getItem(checkoutKey);

		if (!storedCheckout) {
			return null;
		}

		sessionStorage.removeItem(checkoutKey);

		const checkout = JSON.parse(storedCheckout) as PurchaseCheckout;
		const plan = order.getPlan();
		const params: TrackingEventMap['purchase'] = {
			ecommerce: {
				transaction_id: order.getID(),
				value: checkout.value,
				currency: checkout.currency,
				...(checkout.coupon ? { coupon: checkout.coupon } : {}),
				items: [{
					item_id: order.getPlanID(),
					item_name: order.getLocalizedDisplayName(i18n.global.locale),
					item_category: plan.CustomFields.category,
					price: checkout.value,
					quantity: 1,
				}],
			},
			payment_type: checkout.paymentType,
			purchase_type: purchaseTypes[getBillingOccurence(plan)],
		};

		return { checkout, params };
	})().match(
		(purchase) => {
			if (!purchase) {
				return;
			}

			sendEvent('purchase', purchase.params, () => ({
				...getTrackingContext(),
				portal: purchase.checkout.portal,
				signup_type: purchase.checkout.signupType,
			}));
		},
		(cause) => reportTrackingError('purchase', cause),
	);
};

export const trackLegacyEvent = <Name extends LegacyTrackingEventName>(name: Name, payload: LegacyTrackingEventMap[Name]) => {
	getGtm()
		.andThen((gtm) => Result.fromThrowable(() => gtm.trackEvent({ event: name, ...payload }))())
		.mapErr((cause) => reportTrackingError(name, cause));
};

export const trackNewSignup = (authProvider?: string) => {
	trackPlausibleEvent('Signup', null);
	trackLegacyEvent('registrationComplete', {
		signupMethod: authProvider ?? 'unknown',
	});
	consola.info('New User created, successfully tracked signup');
};

const getTransactionCompletePayload = (order: SubscriptionOrder): LegacyTrackingEventMap['transactionComplete'] => ({
	transactionId: order.getID(),
	transactionAffiliation: 'music2me',
	transactionTotal: order.getTotal(),
	transactionTax: order.getTaxes(),
	transactionValue: order.getValue(),
	transactionProducts: [{
		sku: order.getPlanID(),
		name: order.getLocalizedDisplayName(i18n.global.locale),
		category: order.getCategory(),
		price: order.getTotal(),
		quantity: 1,
	}],
});

export const trackOrder = (order: SubscriptionOrder) => {
	Result.fromThrowable(() => ({
		payload: getTransactionCompletePayload(order),
		plausibleGoal: getPlausibleGoal(order),
	}))().match(
		({ payload, plausibleGoal }) => {
			trackLegacyEvent('transactionComplete', payload);
			trackPlausibleEvent(plausibleGoal, { totalInCents: order.totalInCents });
			consola.info('Successfully tracked order', order);
		},
		(cause) => reportTrackingError('transactionComplete', cause),
	);
};

export const trackBranchName = () => {
	trackLegacyEvent('ab_branch', {
		ab_branch: __BRANCH_NAME__,
	});
	setTag('branch', __BRANCH_NAME__);
	consola.info('Successfully tracked branch name', __BRANCH_NAME__);
};
